import { supabase, isSupabaseConfigured, getCurrentUserId } from "@/lib/supabase";
import type { Agent, AgentOutputRequirements, AgentPersona } from "@/types/aiToolkit";
import { getAgentById as getBuiltinAgentById } from "@/data/aiToolkit";
import { isWorkflowUuid } from "@/services/aiWorkflowsService";
import { AGENT_COLOR_PALETTE } from "@/lib/agentColorGenerator";
import { ICON_OPTIONS } from "@/lib/agentBuildConstants";

interface DatabaseAgent {
  id: string;
  user_id: string;
  name: string;
  description: string;
  node_type: string;
  category: string;
  icon: string;
  color: string;
  tags: string[];
  access: string;
  persona: AgentPersona | string;
  capability_modules: string[];
  memory_policy: {
    type: 'none' | 'session' | 'persistent';
    retentionDays?: number;
    maxMemoryItems?: number;
    includeContext?: boolean;
  };
  collaboration_rules?: {
    canDelegate: boolean;
    canReceiveDelegation: boolean;
    handoffProtocol?: string;
    sharedContextKeys?: string[];
  };
  preferred_model?: string;
  context_window?: number;
  max_iterations?: number;
  is_template?: boolean;
  composed_agent_ids?: string[];
  folder_id?: string | null;
  lens?: string | null;
  expert_focus?: string | null;
  output_requirements?: AgentOutputRequirements | Record<string, unknown>;
  linked_source_ids?: string[];
  training_document_ids?: string[];
  integration_scopes?: string[];
  execution_mode?: string;
  output_schema?: Record<string, unknown>;
  step_role?: string | null;
  action_permissions?: Record<string, unknown>;
  confidence_policy?: Record<string, unknown>;
  quality_policy?: Record<string, unknown>;
  attached_skill_ids?: string[];
  custom_instructions?: string | null;
  worker_profile_id?: string | null;
  allowed_tool_ids?: string[];
  created_at: string;
  updated_at: string;
}

export interface InsertAgent {
  name: string;
  description: string;
  nodeType: string;
  category: string;
  icon: string;
  color: string;
  tags: string[];
  access: string;
  persona: AgentPersona;
  capabilityModules: string[];
  memoryPolicy: {
    type: 'none' | 'session' | 'persistent';
    retentionDays?: number;
    maxMemoryItems?: number;
    includeContext?: boolean;
  };
  collaborationRules?: {
    canDelegate: boolean;
    canReceiveDelegation: boolean;
    handoffProtocol?: string;
    sharedContextKeys?: string[];
  };
  preferredModel?: string;
  contextWindow?: number;
  maxIterations?: number;
  isTemplate?: boolean;
  composedAgentIds?: string[];
  folderId?: string | null;
  lens?: string | null;
  expertFocus?: string | null;
  outputRequirements?: AgentOutputRequirements;
  linkedSourceIds?: string[];
  trainingDocumentIds?: string[];
  integrationScopes?: string[];
  executionMode?: 'manual' | 'automatic' | 'both';
  outputSchema?: Record<string, unknown>;
  stepRole?: string | null;
  actionPermissions?: Record<string, unknown>;
  confidencePolicy?: Record<string, unknown>;
  qualityPolicy?: Record<string, unknown>;
  attachedSkillIds?: string[];
  customInstructions?: string | null;
  workerProfileId?: string | null;
  allowedToolIds?: string[];
}

function normalizePersona(raw: unknown): AgentPersona {
  if (typeof raw === "string") {
    return {
      role: "",
      expertise: [],
      communicationStyle: "professional",
      systemPrompt: raw,
    };
  }
  if (raw && typeof raw === "object") {
    const p = raw as Record<string, unknown>;
    return {
      role: typeof p.role === "string" ? p.role : "",
      expertise: Array.isArray(p.expertise) ? (p.expertise as string[]) : [],
      communicationStyle: typeof p.communicationStyle === "string" ? p.communicationStyle : "professional",
      systemPrompt: typeof p.systemPrompt === "string" ? p.systemPrompt : "",
    };
  }
  return {
    role: "",
    expertise: [],
    communicationStyle: "professional",
    systemPrompt: "",
  };
}

function normalizeOutputRequirements(raw: unknown): AgentOutputRequirements | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const format = o.format;
  const validFormats = ["text", "markdown", "json", "list"] as const;
  return {
    format: validFormats.includes(format as typeof validFormats[number])
      ? (format as AgentOutputRequirements["format"])
      : undefined,
    instructions: typeof o.instructions === "string" ? o.instructions : undefined,
    requiredSections: Array.isArray(o.requiredSections)
      ? (o.requiredSections as string[]).filter((s) => typeof s === "string")
      : undefined,
    maxLengthHint: typeof o.maxLengthHint === "string" ? o.maxLengthHint : undefined,
  };
}

class AIAgentsService {
  async getAgents(): Promise<Agent[]> {
    if (!isSupabaseConfigured) return [];
    const userId = await getCurrentUserId();
    if (!userId) return [];

    const { data, error } = await supabase
      .from("ai_agents")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching agents:", error);
      return [];
    }

    return (data || []).map((row) => this.mapToAgent(row as DatabaseAgent));
  }

  async getAgentById(id: string): Promise<Agent | null> {
    const builtin = getBuiltinAgentById(id);
    if (builtin) return builtin;

    if (!isSupabaseConfigured) return null;
    if (!isWorkflowUuid(id)) return null;

    const userId = await getCurrentUserId();
    if (!userId) return null;

    const { data, error } = await supabase
      .from("ai_agents")
      .select("*")
      .eq("id", id)
      .eq("user_id", userId)
      .single();

    if (error) {
      console.error("Error fetching agent:", error);
      return null;
    }

    return data ? this.mapToAgent(data as DatabaseAgent) : null;
  }

  async createAgent(agent: InsertAgent): Promise<Agent | null> {
    if (!isSupabaseConfigured) return null;
    const userId = await getCurrentUserId();
    if (!userId) return null;

    // Auto-assign color and icon based on agent count (cycling through palettes)
    let assignedColor = agent.color;
    let assignedIcon = agent.icon;

    if (!assignedColor || assignedColor === "bg-purple-500" || !assignedIcon || assignedIcon === ICON_OPTIONS[0]) {
      const { count: existingAgentCount } = await supabase
        .from("ai_agents")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .then((res) => ({ count: res.count || 0 }));

      if (!assignedColor || assignedColor === "bg-purple-500") {
        const colorIndex = existingAgentCount % AGENT_COLOR_PALETTE.length;
        assignedColor = AGENT_COLOR_PALETTE[colorIndex];
      }

      if (!assignedIcon || assignedIcon === ICON_OPTIONS[0]) {
        const iconIndex = existingAgentCount % ICON_OPTIONS.length;
        assignedIcon = ICON_OPTIONS[iconIndex];
      }
    }

    const insertPayload: Record<string, unknown> = {
      user_id: userId,
      name: agent.name,
      description: agent.description,
      node_type: agent.nodeType,
      category: agent.category,
      icon: assignedIcon,
      color: assignedColor,
      tags: agent.tags && agent.tags.length > 0 ? agent.tags : (agent.persona?.expertise || []),
      access: agent.access || "free",
      persona: agent.persona,
      capability_modules: agent.capabilityModules || [],
      memory_policy: agent.memoryPolicy,
      output_requirements: agent.outputRequirements ?? {},
      linked_source_ids: agent.linkedSourceIds ?? [],
      training_document_ids: agent.trainingDocumentIds ?? [],
    };

    if (agent.lens) insertPayload.lens = agent.lens;
    if (agent.expertFocus) insertPayload.expert_focus = agent.expertFocus;
    if (agent.folderId) insertPayload.folder_id = agent.folderId;
    if (agent.collaborationRules) insertPayload.collaboration_rules = agent.collaborationRules;
    if (agent.preferredModel) insertPayload.preferred_model = agent.preferredModel;
    if (agent.contextWindow) insertPayload.context_window = agent.contextWindow;
    if (agent.maxIterations) insertPayload.max_iterations = agent.maxIterations;
    if (agent.isTemplate !== undefined) insertPayload.is_template = agent.isTemplate;
    if (agent.composedAgentIds) insertPayload.composed_agent_ids = agent.composedAgentIds;
    if (agent.integrationScopes) insertPayload.integration_scopes = agent.integrationScopes;
    if (agent.executionMode) insertPayload.execution_mode = agent.executionMode;
    if (agent.outputSchema) insertPayload.output_schema = agent.outputSchema;
    if (agent.stepRole) insertPayload.step_role = agent.stepRole;
    if (agent.actionPermissions) insertPayload.action_permissions = agent.actionPermissions;
    if (agent.confidencePolicy) insertPayload.confidence_policy = agent.confidencePolicy;
    if (agent.qualityPolicy) insertPayload.quality_policy = agent.qualityPolicy;
    if (agent.attachedSkillIds) insertPayload.attached_skill_ids = agent.attachedSkillIds;
    if (agent.customInstructions) insertPayload.custom_instructions = agent.customInstructions;
    if (agent.workerProfileId) insertPayload.worker_profile_id = agent.workerProfileId;
    if (agent.allowedToolIds) insertPayload.allowed_tool_ids = agent.allowedToolIds;

    const { data, error } = await supabase
      .from("ai_agents")
      .insert(insertPayload)
      .select()
      .single();

    if (error) {
      console.error("Error creating agent:", error);
      throw new Error(error.message || "Failed to create agent");
    }

    return data ? this.mapToAgent(data as DatabaseAgent) : null;
  }

  async updateAgent(id: string, updates: Partial<InsertAgent>): Promise<Agent | null> {
    if (!isSupabaseConfigured) return null;
    const userId = await getCurrentUserId();
    if (!userId) return null;

    const updatePayload: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };

    if (updates.name !== undefined) updatePayload.name = updates.name;
    if (updates.description !== undefined) updatePayload.description = updates.description;
    if (updates.nodeType !== undefined) updatePayload.node_type = updates.nodeType;
    if (updates.category !== undefined) updatePayload.category = updates.category;
    if (updates.icon !== undefined) updatePayload.icon = updates.icon;
    if (updates.color !== undefined) updatePayload.color = updates.color;
    if (updates.tags !== undefined) updatePayload.tags = updates.tags;
    if (updates.access !== undefined) updatePayload.access = updates.access;
    if (updates.persona !== undefined) updatePayload.persona = updates.persona;
    if (updates.capabilityModules !== undefined) updatePayload.capability_modules = updates.capabilityModules;
    if (updates.memoryPolicy !== undefined) updatePayload.memory_policy = updates.memoryPolicy;
    if (updates.collaborationRules !== undefined) updatePayload.collaboration_rules = updates.collaborationRules;
    if (updates.preferredModel !== undefined) updatePayload.preferred_model = updates.preferredModel;
    if (updates.contextWindow !== undefined) updatePayload.context_window = updates.contextWindow;
    if (updates.maxIterations !== undefined) updatePayload.max_iterations = updates.maxIterations;
    if (updates.isTemplate !== undefined) updatePayload.is_template = updates.isTemplate;
    if (updates.composedAgentIds !== undefined) updatePayload.composed_agent_ids = updates.composedAgentIds;
    if (updates.folderId !== undefined) updatePayload.folder_id = updates.folderId;
    if (updates.lens !== undefined) updatePayload.lens = updates.lens || null;
    if (updates.expertFocus !== undefined) updatePayload.expert_focus = updates.expertFocus || null;
    if (updates.outputRequirements !== undefined) updatePayload.output_requirements = updates.outputRequirements;
    if (updates.linkedSourceIds !== undefined) updatePayload.linked_source_ids = updates.linkedSourceIds;
    if (updates.trainingDocumentIds !== undefined) updatePayload.training_document_ids = updates.trainingDocumentIds;
    if (updates.integrationScopes !== undefined) updatePayload.integration_scopes = updates.integrationScopes;
    if (updates.executionMode !== undefined) updatePayload.execution_mode = updates.executionMode;
    if (updates.outputSchema !== undefined) updatePayload.output_schema = updates.outputSchema;
    if (updates.stepRole !== undefined) updatePayload.step_role = updates.stepRole;
    if (updates.actionPermissions !== undefined) updatePayload.action_permissions = updates.actionPermissions;
    if (updates.confidencePolicy !== undefined) updatePayload.confidence_policy = updates.confidencePolicy;
    if (updates.qualityPolicy !== undefined) updatePayload.quality_policy = updates.qualityPolicy;
    if (updates.attachedSkillIds !== undefined) updatePayload.attached_skill_ids = updates.attachedSkillIds;
    if (updates.customInstructions !== undefined) updatePayload.custom_instructions = updates.customInstructions;
    if (updates.workerProfileId !== undefined) updatePayload.worker_profile_id = updates.workerProfileId;
    if (updates.allowedToolIds !== undefined) updatePayload.allowed_tool_ids = updates.allowedToolIds;

    const { data, error } = await supabase
      .from("ai_agents")
      .update(updatePayload)
      .eq("id", id)
      .eq("user_id", userId)
      .select()
      .single();

    if (error) {
      console.error("Error updating agent:", error);
      throw new Error(error.message || "Failed to update agent");
    }

    return data ? this.mapToAgent(data as DatabaseAgent) : null;
  }

  async deleteAgent(id: string): Promise<boolean> {
    if (!isSupabaseConfigured) return false;
    const userId = await getCurrentUserId();
    if (!userId) return false;

    const { error } = await supabase
      .from("ai_agents")
      .delete()
      .eq("id", id)
      .eq("user_id", userId);

    if (error) {
      console.error("Error deleting agent:", error);
      throw new Error(error.message || "Failed to delete agent");
    }

    return true;
  }

  private mapToAgent(data: DatabaseAgent): Agent {
    const row = data as DatabaseAgent & Record<string, unknown>;
    const outputRaw = row.output_requirements ?? row.outputRequirements;
    const linkedRaw = row.linked_source_ids ?? (row.linkedSourceIds as string[] | undefined);
    const trainingRaw = row.training_document_ids ?? (row.trainingDocumentIds as string[] | undefined);

    return {
      id: data.id,
      name: data.name,
      description: data.description,
      tier: "agent",
      nodeType: data.node_type as Agent["nodeType"],
      category: data.category,
      icon: data.icon,
      color: data.color,
      tags: data.tags || [],
      access: data.access as Agent["access"],
      persona: normalizePersona(data.persona),
      capabilityModules: data.capability_modules || [],
      memoryPolicy: data.memory_policy,
      collaborationRules: data.collaboration_rules,
      preferredModel: data.preferred_model,
      contextWindow: data.context_window,
      maxIterations: data.max_iterations,
      isTemplate: data.is_template || false,
      composedAgentIds: data.composed_agent_ids,
      folderId: data.folder_id ?? null,
      lens: row.lens ?? null,
      expertFocus: row.expert_focus ?? (row.expertFocus as string | undefined) ?? null,
      outputRequirements: normalizeOutputRequirements(outputRaw),
      linkedSourceIds: Array.isArray(linkedRaw) ? linkedRaw : [],
      trainingDocumentIds: Array.isArray(trainingRaw) ? trainingRaw : [],
      integrationScopes: (row.integration_scopes as string[]) ?? [],
      executionMode: (row.execution_mode as Agent["executionMode"]) ?? "manual",
      outputSchema: row.output_schema as Record<string, unknown> | undefined,
      stepRole: (row.step_role as string) ?? null,
      actionPermissions: (row.action_permissions as Record<string, unknown>) ?? {},
      confidencePolicy: row.confidence_policy as Agent["confidencePolicy"],
      qualityPolicy: row.quality_policy as Agent["qualityPolicy"],
      attachedSkillIds: (row.attached_skill_ids as string[]) ?? [],
      customInstructions: (row.custom_instructions as string) ?? null,
      workerProfileId: (row.worker_profile_id as string) ?? null,
      allowedToolIds: (row.allowed_tool_ids as string[]) ?? [],
      createdAt: data.created_at,
      createdBy: "user",
    };
  }
}

export const aiAgentsService = new AIAgentsService();

export function agentToInsertPayload(agent: Partial<Agent>): InsertAgent {
  return {
    name: agent.name || "",
    description: agent.description || "",
    nodeType: agent.nodeType || "specialist",
    category: agent.category || "general",
    icon: agent.icon || "Bot",
    color: agent.color || "purple",
    tags: agent.tags || [],
    access: agent.access || "free",
    persona: agent.persona || {
      role: "",
      expertise: [],
      communicationStyle: "professional",
      systemPrompt: "",
    },
    capabilityModules: agent.capabilityModules || [],
    memoryPolicy: agent.memoryPolicy || { type: "session", includeContext: true },
    collaborationRules: agent.collaborationRules,
    preferredModel: agent.preferredModel,
    contextWindow: agent.contextWindow,
    maxIterations: agent.maxIterations,
    isTemplate: agent.isTemplate,
    composedAgentIds: agent.composedAgentIds,
    folderId: agent.folderId ?? null,
    lens: agent.lens ?? null,
    expertFocus: agent.expertFocus ?? null,
    outputRequirements: agent.outputRequirements,
    linkedSourceIds: agent.linkedSourceIds ?? [],
    trainingDocumentIds: agent.trainingDocumentIds ?? [],
    integrationScopes: agent.integrationScopes ?? [],
    executionMode: agent.executionMode ?? "manual",
    outputSchema: agent.outputSchema,
    stepRole: agent.stepRole ?? null,
    actionPermissions: agent.actionPermissions,
    confidencePolicy: agent.confidencePolicy,
    qualityPolicy: agent.qualityPolicy,
    attachedSkillIds: agent.attachedSkillIds ?? [],
    customInstructions: agent.customInstructions ?? null,
    workerProfileId: agent.workerProfileId ?? null,
    allowedToolIds: agent.allowedToolIds ?? [],
  };
}
